// LLM generated - Model Claude Opus 4
import type { Channel } from 'pusher-js';

import pusherClient from '~/config/initPusher';
import { logError } from '~/common/utils/log';
import type { GameStatus, TeamState } from '~/domains/contest/domains/services/interfaces/propsService/api/game';

import { useEntitiesStore, type GameEventUpdate } from '../state/entities.store';

const GAME_UPDATE_EVENT = 'update';
const CHANNEL_PREFIX = 'game-';

/**
 * Payload structure for game updates from Pusher
 */
export interface GameUpdatePayload {
  message: {
    id: string;
    status: GameStatus;
    state?: {
      phase?: string;
      quarter?: number;
      half?: number;
      period?: number;
      clock?: string;
      current_inning?: number;
      current_inning_phase?: 'top' | 'bottom';
      outs?: number;
    };
    home: {
      id: string;
      points?: number;
      runs?: number;
      alias?: string;
    };
    away: {
      id: string;
      points?: number;
      runs?: number;
      alias?: string;
    };
  };
  metadata?: {
    id?: string;
    timestamp?: number;
  };
}

/**
 * Pending event update for batching
 */
type PendingEventUpdate = {
  eventId: string;
  update: GameEventUpdate;
};

export function getGameChannelName(eventId: string): string {
  return `${CHANNEL_PREFIX}${eventId}`;
}

function getEventIdFromChannel(channelName: string): string {
  return channelName.replace(CHANNEL_PREFIX, '');
}

class GameLiveUpdatesManager {
  private subscriptions = new Map<string, Set<string>>();

  private channels = new Map<string, Channel>();

  private isDisconnectedForVisibility = false;

  // Batching state - buffer updates and flush on requestAnimationFrame
  private pendingUpdates = new Map<string, PendingEventUpdate>();

  private batchScheduled = false;

  subscribe(source: string, eventId: string): void {
    const channelName = getGameChannelName(eventId);
    const sources = this.subscriptions.get(channelName) ?? new Set();

    if (sources.has(source)) {
      return;
    }

    if (sources.size === 0) {
      try {
        const channel = pusherClient.subscribe(channelName);
        channel.bind(GAME_UPDATE_EVENT, this.handleUpdate);
        this.channels.set(channelName, channel);
      } catch (error) {
        logError('GameLiveUpdatesManager: Failed to subscribe', { error, channelName, source });
        return;
      }
    }

    sources.add(source);
    this.subscriptions.set(channelName, sources);
  }

  unsubscribe(source: string, eventId: string): void {
    const channelName = getGameChannelName(eventId);
    const sources = this.subscriptions.get(channelName);

    if (!sources) {
      return;
    }

    sources.delete(source);

    if (sources.size === 0) {
      const channel = this.channels.get(channelName);
      if (channel) {
        channel.unbind(GAME_UPDATE_EVENT, this.handleUpdate);
        pusherClient.unsubscribe(channelName);
        this.channels.delete(channelName);
      }
      this.subscriptions.delete(channelName);
    }
  }

  private handleUpdate = (data: GameUpdatePayload): void => {
    const { message } = data;
    const eventId = message.id;

    if (!eventId) {
      logError('GameLiveUpdatesManager: Received update without event ID', { data });
      return;
    }

    // Build the partial update for the event
    const homeUpdate: Partial<TeamState> = {
      id: message.home.id,
    };
    if (message.home.points !== undefined) {
      homeUpdate.points = message.home.points;
    }
    if (message.home.runs !== undefined) {
      homeUpdate.runs = message.home.runs;
    }
    if (message.home.alias !== undefined) {
      homeUpdate.alias = message.home.alias;
    }

    const awayUpdate: Partial<TeamState> = {
      id: message.away.id,
    };
    if (message.away.points !== undefined) {
      awayUpdate.points = message.away.points;
    }
    if (message.away.runs !== undefined) {
      awayUpdate.runs = message.away.runs;
    }
    if (message.away.alias !== undefined) {
      awayUpdate.alias = message.away.alias;
    }

    // Buffer the update and schedule a batched flush
    this.pendingUpdates.set(eventId, {
      eventId,
      update: {
        status: message.status,
        home: homeUpdate as TeamState,
        away: awayUpdate as TeamState,
        state: message.state,
      },
    });
    this.scheduleFlush();
  };

  /**
   * Schedules a batched flush of pending updates on the next animation frame
   */
  private scheduleFlush(): void {
    if (!this.batchScheduled) {
      this.batchScheduled = true;
      requestAnimationFrame(() => {
        this.flushUpdates();
        this.batchScheduled = false;
      });
    }
  }

  /**
   * Flushes all pending updates to the store in a single batch
   */
  private flushUpdates(): void {
    if (this.pendingUpdates.size === 0) {
      return;
    }

    const updates = Array.from(this.pendingUpdates.values());
    this.pendingUpdates.clear();

    // Use batch update to trigger a single store update
    useEntitiesStore.getState().updateEventsBatch(updates);
  }

  getSubscriptionCount(): number {
    return this.subscriptions.size;
  }

  getSourcesForEvent(eventId: string): string[] {
    const channelName = getGameChannelName(eventId);
    const sources = this.subscriptions.get(channelName);
    return sources ? Array.from(sources) : [];
  }

  handleVisibilityChange(isVisible: boolean): void {
    if (!isVisible && !this.isDisconnectedForVisibility) {
      // Note: We don't disconnect here as marketLiveUpdatesManager handles the shared pusher connection
      this.isDisconnectedForVisibility = true;
    } else if (isVisible && this.isDisconnectedForVisibility) {
      this.isDisconnectedForVisibility = false;
    }
  }

  hasActiveSubscriptions(): boolean {
    return this.subscriptions.size > 0;
  }
}

export const gameLiveUpdatesManager = new GameLiveUpdatesManager();
